import {Component, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {environment} from '../../environments/environment';
import {HttpClient} from "@angular/common/http";
import {UntypedFormGroup} from "@angular/forms";
import {TranslateService} from '@ngx-translate/core';
import {EingabeForm} from "./eingabe/eingabe.form";
import {PortalService} from "../services/portal.service";
import {DecimalPipe} from "@angular/common";
import {ElementService} from "../services/element.service";
import {BackendService} from "../services/backend.service";
import {Subscription} from "rxjs";
import {EmbedService} from "../services/embed.service";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";

declare const minicheckAction: any;

/**
 * Minicheck component, show and calculate a minicheck
 */
@Component({
    selector: 'app-minicheck-root',
    templateUrl: './minicheck.component.html',
    styleUrls: environment.production === true ? [] : ['../../../../scss/minicheck.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [DecimalPipe],
    standalone: false
})
export class MinicheckComponent implements OnInit, OnDestroy {

  /**
   * The formGroup, created by the formConfig
   */
  @Input() form: UntypedFormGroup;

  /**
   * Configuration of a form
   */
  formConfig: EingabeForm;

  /**
   * Holds the action object
   */
  minicheckAction: any;
  /**
   * Holds the name of a single action
   */
  actionName: string;

  /**
   * Holds the category of a single action
   */
  category: string;

  /**
   * Holds the advisor teaser that should be displayed of a single action
   */
  advisorTeaser: string;

  /**
   * Holds the options of a single parameter
   */
  options: any = [];

  /**
   * if set to a string value, a form error is shown
   */
  formError: string = null;

  /**
   * indicator if a result is avaibalbe
   */
  hasResult: boolean = false;

  /**
   * Holds the result object from calculation backend
   */
  result: any;

  /**
   * URL for showing the STC
   */
  stcUrl: string;

  /**
   * URL for showing the TEC
   */
  tecUrl: string;

  /**
   * URL for showing the HEC
   */
  hecUrl: string;

  /**
   * URL for showing the WAC
   */
  wacUrl: string;

  /**
   * URL for showing the MOC
   */
  mocUrl: string = null;


  /**
   * placeholder for dynamic outputs
   */
  placeholder: any = {};

  /**
   * update textkey postfix for single person equivalent
   */
  postfix: string;

  /**
   * update textkey postfix for closing coal station
   */
  postfixCoal: string;

  /**
   * set to true if money saving > 0 and co2 saving is 0
   */
  isOnlyMoneySaving: boolean = false;

  /**
   * set to true if money saving > 0
   */
  isPositiveSavingEur: boolean = false;

  /**
   * set to true if co2 saving > 0
   */
  isPositiveSavingCo2: boolean = false;

  /**
   * set to true if money saving < 0
   */
  isNegativeSavingEur: boolean = false;

  /**
   * set to true if co2 saving < 0
   */
  isNegativeSavingCo2: boolean = false;

  /**
   * set to true if co2 saving is >= 1.1 tonnes
   */
  co2Tonnes: boolean = false;

  /**
   * set to true if co2 saving is one ton
   */
  co2OneTon: boolean = false;

  /**
   * set to true if a field has inline info in special cases
   */
  showInlineInfo: boolean = false;

  /**
   * set to true if button should be disabled in special cases
   */
  isButtonDisabled: boolean = false;

  /**
   * set to true if STC Teaser should be displayed
   */
  showStcTeaser: boolean = false;

  /**
   * set to true if WAC Teaser should be displayed
   */
  showWacTeaser: boolean = false;

  /**
   * set to true if HEC Teaser should be displayed
   */
  showHecTeaser: boolean = false;

  /**
   * set to true if TEC Teaser should be displayed
   */
  showTecTeaser: boolean = false;

  /**
   * set to true if MOC Teaser should be displayed
   */
  showMocTeaser: boolean = false;

  /**
   * set to true we need user input to calculate action
   */
  isInputNeeded: boolean = false;

  /**
   * Change subscribtion
   */
  changeSubscription: Subscription;

  /**
   * set to true if we have an updated result and to show callout
   */
  showUpdatedResult: boolean = false;

  /**
   * Check if it is an internal portal
   */
  isInternalPortal: boolean = false;

  /**
   * Toggle to force the Dialog/NL-Box
   */
  forceDialog: boolean = false;

  /**
   * holds the data that we need for the dialogform.
   */
  dialogData: any = {};

  /**
   * constructor
   * @param http
   * @param translate
   * @param embedService
   * @param portalService
   * @param decimalPipe
   * @param elementService
   * @param backendService
   */
  constructor(private http: HttpClient, private translate: TranslateService, protected embedService: EmbedService,
              protected portalService: PortalService, protected decimalPipe: DecimalPipe, protected elementService: ElementService,
              protected backendService: BackendService) {
    // init translator
    translate.addLangs(['de']);
    translate.setFallbackLang('de');
    translate.use('de');
  }

  /**
   * @inheritDoc
   */
  ngOnInit() {
    // get the name of minicheck
    if(minicheckAction) {
      this.minicheckAction = minicheckAction;
      this.actionName = minicheckAction.action_name;
      this.category = minicheckAction.action_category;
      this.advisorTeaser = minicheckAction.advisor_teaser;
    }

    // if embedded resize window
    this.embedService.init();

    if (this.embedService.isEmbedded()) {
      this.embedService.triggerFakeScroll();
    }

    // check if internal portal
    this.isInternalPortal = this.portalService.isInternalPortal();

    this.initDialogData();
    // should we force to see the dialog box
    this.forceDialog = this.portalService.getConfigAsBool('embed.newsletter.force');

    // set advisor name for textkey prefix in info text
    this.backendService.setAdvisor('minicheck');

    // add formConfig and FormGroup and set the data
    this.formConfig = new EingabeForm();
    this.formConfig.setActionName(this.actionName);
    this.form = this.formConfig.getForm(this.actionName);
    this.formConfig.setData(this.form.value);

    // initial no updated result, so set to false
    this.showUpdatedResult = false;

    // check if form changes and subscribe on it
    this.changeSubscription = this.form.valueChanges.subscribe(() => {
      // check if we have a result and it's not a special case where there is no better option to choose
      if(this.hasResult && !this.showInlineInfo) {
        setTimeout(() => this.calculateAction(), 0);
        this.showUpdatedResult = !this.isInputNeeded;

        // set to false after short period of time to show loading animation
        setTimeout(() => this.showUpdatedResult = false, 1000);
      }
    });

    // action 'housing_sufficiency': update values for new and current living area
    if(this.actionName === 'housing_sufficiency' && this.form?.controls?.current_living_area && this.form?.controls?.new_living_area) {
      // new area may not be bigger than current area, set the new value maximum same as current value in form
      this.form.get('current_living_area').valueChanges.pipe(
        // apply changes after short period of time
        debounceTime(300),
        // subscribe only if there are real value changes
        distinctUntilChanged()
      ).subscribe(currentAreaValue => {
        const newLivingArea = this.form.get('new_living_area').value;
        // check if new living area is bigger than current
        if (newLivingArea > currentAreaValue) {
          // move down the new value to current value
          this.form.get('new_living_area').setValue(currentAreaValue);
        }
      });

      // current area may not be smaller than new area, set the current value minimum same as new value in form
      this.form.get('new_living_area').valueChanges.pipe(
        // apply changes after short period of time
        debounceTime(300),
        // subscribe only if there are real value changes
        distinctUntilChanged()
      ).subscribe(newAreaValue => {
        const currentLivingArea = this.form.get('current_living_area').value;
        // check if current living area is smaller than new area
        if (currentLivingArea < newAreaValue) {
          // move up the current value to at least to new living area value
          this.form.get('current_living_area').setValue(newAreaValue);
        }
      });
    }

    // set inline info to true and disable button if field value of current_bio_product_proportion === 100%
    if(this.actionName === 'buy_more_bio_products' && this.form.controls?.current_bio_product_proportion) {
      this.form?.get('current_bio_product_proportion').valueChanges.subscribe((selectedValue: string) => {
        // show inline info and disable button if no better option
        this.showInlineInfo = (selectedValue === '100');
        this.isButtonDisabled = (selectedValue === '100');
        this.showUpdatedResult = false;
        // set hasResult to false in order to hide old result
        if(this.showInlineInfo) {
          this.hasResult = false;
          this.isInputNeeded = false;
        }
      });
    }

    // set inline info to true and disable button if field value of current_nutrition_style === vegan
    if(this.actionName === 'change_nutrition_style' && this.form.controls?.current_nutrition_style) {
      this.form?.get('current_nutrition_style').valueChanges.subscribe((selectedValue: string) => {
        // show inline info and disable button if no better option
        this.showInlineInfo = (selectedValue === 'vegan');
        this.isButtonDisabled = (selectedValue === 'vegan');
        this.showUpdatedResult = false;
        // set hasResult to false in order to not show old result
        if(this.showInlineInfo) {
          this.hasResult = false;
          this.isInputNeeded = false;
        }
      });
    }

    // for action insulation: set inline info to true and dieable button if building_year === after_2001
    if(this.actionName === 'insulation' && this.form?.controls['building_year']) {
      this.form.get('building_year').valueChanges.subscribe((selectedValue: string) => {
        // show inline info and disable submit button
        this.showInlineInfo = (selectedValue === 'after_2001');
        this.isButtonDisabled = (selectedValue === 'after_2001');
        this.showUpdatedResult = false;

        // set hasResult to false in order to not show old result
        if (this.showInlineInfo) {
          this.hasResult = false;
          this.isInputNeeded = false;
        }
      });
    }

    // check which advisor teaser should be displayed and set to true
    this.showStcTeaser = this.advisorTeaser.includes('stromcheck');
    this.showWacTeaser = this.advisorTeaser.includes('wassercheck');
    this.showHecTeaser = this.advisorTeaser.includes('heizcheck');
    this.showTecTeaser = this.advisorTeaser.includes('thermostatcheck');
    this.showMocTeaser = this.advisorTeaser.includes('modernisierungscheck');

    // init url to lead to advisor teaser
    this.stcUrl = this.portalService.getConfig("alladvisors.link.stromcheck")?.replace(/\{\{portalId}}/,
      this.portalService.currentPortalId);
    this.tecUrl = this.portalService.getConfig("alladvisors.link.thermostatcheck")?.replace(/\{\{portalId}}/,
      this.portalService.currentPortalId);
    this.hecUrl = this.portalService.getConfig("alladvisors.link.heizcheck")?.replace(/\{\{portalId}}/,
      this.portalService.currentPortalId);
    this.wacUrl = this.portalService.getConfig("alladvisors.link.wassercheck")?.replace(/\{\{portlId}}/,
      this.portalService.currentPortalId);
    this.mocUrl = this.portalService.getConfig("alladvisors.link.modernisierungscheck")?.replace(/\{\{portlId}}/,
      this.portalService.currentPortalId);
  }

  /**
   * Unsubscribe from value change on form
   */
  ngOnDestroy() {
    this.changeSubscription.unsubscribe();
  }

  /**
   * Get the result from the backend
   */
  calculateAction(): void {
    // set formError to null
    this.formError = null;

    // check if form is valid
    if(this.form.valid) {
      // send parameter to backend

        this.http.post(document.location.href, this.formConfig.getData()).subscribe(result => {
          // show result
          this.result = result;
          this.isSavingPositiveOrNegative();
          this.initPlaceholder();
          this.updateTextkeyPostfix();
          if (this.hasResult === false) {
            this.scrollToResult();
          }

          this.hasResult = true;
        }, () => {
          // show error message because calculation did not work
          this.formError = 'internal';
        });
    }
    else {
      // mark all fields as touched
      for(const field in this.form.controls) {
        if(this.form.controls[field].valid === false) {
          this.form.controls[field].markAsTouched({onlySelf: true});
        }
      }

      // reset result
      this.hasResult = false;
      this.showUpdatedResult = false;

      // init form errors for different cases
      this.initFormError();
    }
  }

  /**
   * Init placeholder
   */
  initPlaceholder(): void {
    this.placeholder = {
      co2Saving: this.result.savingCo2,
      eurSaving: this.decimalPipe.transform(Math.abs(this.result.savingEur), '1.0-0'),
      kWhSaving: this.decimalPipe.transform(this.result.savingkWh, '1.0-0'),
      kmOnePerson: this.decimalPipe.transform(this.result.equalToKmPublicTransportOnePerson, '1.0-0'),
      kmGroup: this.decimalPipe.transform(this.result.equalToKmPublicTransportGroup, '1.0-0'),
      totalDays: this.decimalPipe.transform(this.result.equalToClosingCoalPowerStation, '1.0-2'),
      pluralHours: ''
    };

    // reset initial value
    this.co2Tonnes = false;
    this.co2OneTon = false;

    // convert co2 savings from kilos into tonnes
    const tonnes = (this.result.savingCo2 / 1000);

    // check if we have more than one ton co2 saving
    if(this.result?.savingCo2 >= 1050 && tonnes % 1 !== 0) {
      // round to one digit after comma
      this.placeholder.co2SavingTonnes = this.decimalPipe.transform(tonnes, '1.0-1');
      this.co2Tonnes = true;
    }
    // check if we display exactly one ton co2 saving
    if(this.result?.savingCo2 >= 1000 && this.result?.savingCo2 < 1050) {
      this.co2OneTon = true;
      this.placeholder.co2SavingOneTon = 1;
    }

    // split total hours in hours and minutes
    const totalHours = this.result?.equalToHoursStreaming;
    this.placeholder.hoursStreaming = Math.floor(this.result?.equalToHoursStreaming);
    const totalMinutes = (totalHours - this.placeholder.hoursStreaming) * 60;
    this.placeholder.minutesStreaming = Math.round(totalMinutes / 5) * 5;

    // convert every 60 minutes into 1 hour
    if(this.placeholder.minutesStreaming === 60) {
      this.placeholder.hoursStreaming += 1;
      this.placeholder.minutesStreaming = 0;
    }

    // show min 5 minutes if total minutes > 0 and we have co2 saving
    if(this.placeholder.co2Saving > 0 && totalMinutes > 0 && this.placeholder.minutesStreaming < 5 && this.placeholder.hoursStreaming < 1) {
      this.placeholder.minutesStreaming = 5;
    }

    // check if hours > 1 and show plural word in text
    if(this.placeholder.hoursStreaming > 1) {
      this.placeholder.pluralHours = 'n';
    }

    // split total days in years/months/days
    this.placeholder.totalDays = this.result?.equalToClosingCoalPowerStation;
    this.placeholder.totalYears = Math.floor(this.placeholder.totalDays / 360);
    const remainingDaysFromYear = this.placeholder.totalDays % 360;

    this.placeholder.totalMonths = Math.floor(remainingDaysFromYear / 30);
    this.placeholder.remainingDays = remainingDaysFromYear % 30;

    // reset to initial value
    this.placeholder.pluralDays = '';
    this.placeholder.pluralMonths = '';
    this.placeholder.pluralYears = '';

    // check if years/days/months > 1 to show plural word in text
    if(this.placeholder.totalDays > 1 && this.placeholder.totalDays < 30 || this.placeholder.remainingDays > 1) {
      this.placeholder.pluralDays = 'e';
    }
    if(this.placeholder.totalMonths > 1) {
      this.placeholder.pluralMonths = 'e';
    }
    if(this.placeholder.totalYears > 1) {
      this.placeholder.pluralYears = 'e';
    }

    // reset to initial value
    this.isInputNeeded = false;
    this.isOnlyMoneySaving = false;

    // check if money saving > 0
    if(this.result?.savingEur > 0 && this.result?.savingCo2 === 0) {
      // show only money saving section
      this.isOnlyMoneySaving = true;
    }

    // check if no money and no co2 saving
    if(this.result?.savingEur === 0 && this.result?.savingCo2 === 0) {
      // ask user for more input
      this.isInputNeeded = true;
      this.showUpdatedResult = false;
    }

  }

  /**
   * update textkeys for equivalents
   */
  updateTextkeyPostfix(): void {
    this.postfix = '.streaming.minutes';

    // update textkey for single person equivalent
    if(this.result?.equalToHoursStreaming > 24) {
      // show public transport equivalent when streaming exceed one day
      this.postfix = '.km.public.transport';
    }
    else {
      if(this.placeholder?.hoursStreaming >= 1 && this.placeholder?.minutesStreaming === 0) {
        // show only hours if there are no minutes
        this.postfix = '.streaming.hours';
      }

      if(this.placeholder?.hoursStreaming >= 1 && this.placeholder?.minutesStreaming !== 0) {
        // show hours and minutes
        this.postfix = '.streaming.hours.minutes';
      }
    }

    // update textkey for germany equivalent
    this.postfixCoal = '.days';

    // show months and days and check if we show also years
    if(this.placeholder?.totalDays >= 30 && this.placeholder?.remainingDays >= 1) {
      if(this.placeholder?.totalMonths >= 1) {
        this.postfixCoal = this.placeholder?.totalYears >= 1 ? '.years.months.days' : '.months.days';
      }
      // show days and check if we show also years
      else if(this.placeholder?.totalMonths < 1) {
        this.postfixCoal = this.placeholder?.totalYears >= 1 ? '.years.days' : '.days';
      }
    }

    // show years or months or both
    if(this.placeholder?.totalDays >= 30 && this.placeholder?.remainingDays < 1) {
      if(this.placeholder?.totalMonths >= 1) {
        this.postfixCoal = this.placeholder?.totalYears >= 1 ? '.years.months' : '.months';
      }
      else if(this.placeholder?.totalMonths < 1 && this.placeholder?.totalYears >= 1) {
        this.postfixCoal = '.years';
      }
    }

  }

  /**
   * Scroll to result
   */
  scrollToResult(): void {
    // show result on top of the screen
    setTimeout(() => document.getElementById('result').scrollIntoView({
      behavior: "smooth",
      block: "start"
    }));
  }

  /**
   * create data object that will be sent to the backend with the registration for the dialog
   */
  initDialogData(): void {
      this.dialogData = {
        requestId: 'dummy',
        bmOptinSource: window.location.href,
        doisource: 'mic',
        portalId: this.portalService.currentPortalId,
        actionName: this.actionName
      };
  }

  /**
   * initialize individual form errors for special cases with fallback
   */
  initFormError(): void {
    // form error for the energy_source fields in 'housing_sufficiency' action
    if (this.actionName === 'housing_sufficiency') {
      // get some values from form controls
      const currentEnergySource = this.form?.controls?.current_energy_source?.value ?? null;
      const currentLivingArea = this.form?.controls?.current_living_area?.value ?? null;
      const newLivingArea = this.form?.controls?.new_living_area?.value ?? null;
      const newEnergySource = this.form?.controls?.new_energy_source?.value ?? null;

      // set individual form error if the two energy sources and the two living areas are the same
      if(currentEnergySource && newEnergySource && currentEnergySource === newEnergySource && currentLivingArea === newLivingArea) {
        this.formError ='sameEnergySource';
      }
      else {
        // fallback form error
        this.formError = 'emptyfield';
      }
    }
    else {
      // fallback form error for all other actions
      this.formError = 'emptyfield';
    }

    // scroll to form error
    this.scrollToError();
  }

  /**
   * set bool values for positive or negative savings
   */
  isSavingPositiveOrNegative() {
    this.isNegativeSavingCo2 = this.result?.savingCo2 < 0;
    this.isPositiveSavingCo2 = this.result?.savingCo2 > 0;
    this.isNegativeSavingEur = this.result?.savingEur < 0;
    this.isPositiveSavingEur = this.result?.savingEur > 0;
  }

  /**
   * scrolls to the form error message
   */
  scrollToError(): void {
    // scroll up to the first error message
    setTimeout(() => {
      if(document.querySelector(".form-errors")) {
        const element = <HTMLElement>document.querySelector(".form-errors");
        if(this.embedService.isEmbedded()) {
          this.embedService.scrollToTop(ElementService.getTopOffset(element));
        }
        else {
          ElementService.scrollToTarget(element);
        }
      }
    }, 200);
  }
}
